import { create } from "zustand";
import { useStoreWithEqualityFn } from "zustand/traditional";
import { shallow } from "zustand/shallow";
import { temporal } from "zundo";
import type { TemporalState } from "zundo";
import {
  LoadedTranscript,
  TranscriptData,
  TranscriptParagraph,
} from "@/shared/interfaces/Transcript";
import { MERGE_MAX_GAP_SECONDS } from "../constants";

let uidCounter = 0;
const nextUid = (): string => `e_${(uidCounter++).toString(36)}`;

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value));

/** Assigns a transient `_uid` to every paragraph so reorders/inserts keep stable keys. */
const withUids = (transcript: TranscriptData): TranscriptData => ({
  ...transcript,
  paragraphs: (transcript.paragraphs ?? []).map((p) => ({
    ...clone(p),
    _uid: p._uid ?? nextUid(),
  })),
});

/** Removes the transient `_uid` from every paragraph before sending to the server. */
export const stripUids = (transcript: TranscriptData): TranscriptData => ({
  ...transcript,
  paragraphs: (transcript.paragraphs ?? []).map((p) => {
    const { _uid: _omit, ...rest } = p;
    void _omit;
    return rest as TranscriptParagraph;
  }),
});

/** Distinct speaker labels referenced by the draft's paragraphs, in first-appearance order. */
export const derivedSpeakers = (transcript: TranscriptData | null): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const p of transcript?.paragraphs ?? []) {
    const label = String(p.speaker ?? "");
    if (!seen.has(label)) {
      seen.add(label);
      out.push(label);
    }
  }
  return out;
};

interface TranscriptDraftState {
  evidenceId: string | null;
  draft: TranscriptData | null;
  /** Frozen load snapshot — for dirty comparison and word-delta baseline. */
  original: TranscriptData | null;
  isInitialized: boolean;
  /** Speaker labels added this session that no entry references yet. */
  sessionSpeakers: string[];
  /** Paragraph _uids whose time range the user (re)confirmed this session. */
  rangeConfirmed: string[];

  initStore: (loaded: LoadedTranscript, evidenceId: string) => void;
  destroyStore: () => void;
  /** Revert the draft to the loaded baseline, dropping all edits. */
  discardChanges: () => void;

  editText: (uid: string, sentenceIdx: number, text: string) => void;
  reassignSpeaker: (uid: string, label: string) => void;
  /**
   * Confirms the AI's existing speaker label for one sentence without changing
   * it — sets `speakerConfidence` to 1 so the low-confidence flag clears. Used
   * by the "verify speaker" affordance when the label is already correct.
   */
  verifySpeaker: (uid: string, sentenceIdx: number) => void;
  deleteEntry: (uid: string) => void;
  /**
   * Removes one sentence from an entry, re-timing the entry to span its
   * remaining sentences. Deleting the last sentence removes the whole entry
   * (no empty entries are left behind).
   */
  deleteSentence: (uid: string, sentenceIdx: number) => void;
  addEntry: (input: {
    label: string;
    start: number;
    end: number;
    text: string;
  }) => string;
  setEntryRange: (uid: string, start: number, end: number) => void;
  /**
   * Reassigns one sentence to `label`, splitting the entry in time order into up
   * to three entries — each spanning its own sentences' actual timestamps:
   *   [sentences before | original speaker]
   *   [the reassigned sentence | label]
   *   [sentences after | original speaker]
   * Leading/trailing segments are omitted when empty. If the source has only
   * that one sentence, it's an in-place speaker change instead.
   */
  splitSentenceToSpeaker: (
    uid: string,
    sentenceIdx: number,
    label: string,
  ) => void;

  addSpeaker: (label: string) => void;
  renameSpeaker: (oldLabel: string, newLabel: string) => void;
  /**
   * Applies a complete original→final label map in a single draft update. Because
   * every paragraph is relabeled from its *original* speaker in one pass, swaps
   * (A→B, B→A) and chains (A→B, B→C) resolve without the intermediate collisions
   * a sequential {@link renameSpeaker} loop would hit. Maps that would merge
   * speakers (duplicate targets, or a target naming an unrenamed existing
   * speaker) are rejected and leave the draft untouched.
   */
  renameSpeakers: (renames: Record<string, string>) => void;
  deleteSpeaker: (label: string) => void;
}

const initialState = {
  evidenceId: null,
  draft: null,
  original: null,
  isInitialized: false,
  sessionSpeakers: [],
  rangeConfirmed: [],
} satisfies Partial<TranscriptDraftState>;

/** Returns a deep-cloned draft with the matching paragraph mutated by `mutate`. */
const mutateParagraph = (
  draft: TranscriptData,
  uid: string,
  mutate: (p: TranscriptParagraph) => void,
): TranscriptData => {
  const next = clone(draft);
  const target = next.paragraphs.find((p) => p._uid === uid);
  if (target) mutate(target);
  return next;
};

const sortByStart = (paragraphs: TranscriptParagraph[]): TranscriptParagraph[] =>
  paragraphs.slice().sort((a, b) => a.start - b.start);

/**
 * Merges the entry `uid` with any immediately adjacent entries that share its
 * speaker AND sit within {@link MERGE_MAX_GAP_SECONDS} of it, into a single
 * entry (start = min start, end = max end, sentences concatenated in order).
 * Paragraphs are kept sorted by start, so list-adjacency equals time-adjacency.
 * The earliest entry in the run keeps its `_uid`; the rest are absorbed.
 * Returns the (possibly unchanged) list plus the surviving uid and the uids
 * that were removed.
 */
const coalesceSameSpeaker = (
  paragraphs: TranscriptParagraph[],
  uid: string,
): {
  paragraphs: TranscriptParagraph[];
  survivingUid: string;
  removedUids: string[];
} => {
  const idx = paragraphs.findIndex((p) => p._uid === uid);
  if (idx === -1) return { paragraphs, survivingUid: uid, removedUids: [] };
  const speaker = String(paragraphs[idx].speaker);

  // Two entries are close enough to merge when the gap between the earlier's end
  // and the later's start is under the threshold (a true overlap is negative).
  const closeEnough = (earlier: TranscriptParagraph, later: TranscriptParagraph) =>
    later.start - earlier.end < MERGE_MAX_GAP_SECONDS;

  // Expand to the run of consecutive same-speaker entries containing idx, only
  // crossing gaps small enough to count as overlapping.
  let lo = idx;
  while (
    lo - 1 >= 0 &&
    String(paragraphs[lo - 1].speaker) === speaker &&
    closeEnough(paragraphs[lo - 1], paragraphs[lo])
  )
    lo--;
  let hi = idx;
  while (
    hi + 1 < paragraphs.length &&
    String(paragraphs[hi + 1].speaker) === speaker &&
    closeEnough(paragraphs[hi], paragraphs[hi + 1])
  )
    hi++;

  if (lo === hi) return { paragraphs, survivingUid: uid, removedUids: [] };

  const run = paragraphs.slice(lo, hi + 1);
  const survivingUid = run[0]._uid as string;
  const removedUids = run.slice(1).map((p) => p._uid as string);
  const merged: TranscriptParagraph = {
    _uid: survivingUid,
    speaker,
    start: Math.min(...run.map((p) => p.start)),
    end: Math.max(...run.map((p) => p.end)),
    sentences: run.flatMap((p) => p.sentences),
  };
  return {
    paragraphs: [
      ...paragraphs.slice(0, lo),
      merged,
      ...paragraphs.slice(hi + 1),
    ],
    survivingUid,
    removedUids,
  };
};

export const useTranscriptDraftStore = create<TranscriptDraftState>()(
  temporal(
    (set) => ({
      ...initialState,

      initStore: (loaded, evidenceId) => {
        const normalized = withUids(loaded.transcript);
        set({
          evidenceId,
          draft: normalized,
          original: clone(normalized),
          isInitialized: true,
          sessionSpeakers: [],
          rangeConfirmed: [],
        });
        useTranscriptDraftStore.temporal.getState().clear();
      },

      destroyStore: () => {
        set(initialState);
        useTranscriptDraftStore.temporal.getState().clear();
      },

      discardChanges: () => {
        set((state) => ({
          draft: state.original ? clone(state.original) : null,
          sessionSpeakers: [],
          rangeConfirmed: [],
        }));
        useTranscriptDraftStore.temporal.getState().clear();
      },

      editText: (uid, sentenceIdx, text) => {
        set((state) => {
          if (!state.draft) return state;
          return {
            draft: mutateParagraph(state.draft, uid, (p) => {
              const sentence = p.sentences[sentenceIdx];
              if (sentence) {
                sentence.text = text;
                // A human re-wrote the content → treat content confidence as verified.
                sentence.confidence = 1;
              }
            }),
          };
        });
      },

      reassignSpeaker: (uid, label) => {
        set((state) => {
          if (!state.draft) return state;
          const relabeled = mutateParagraph(state.draft, uid, (p) => {
            p.speaker = label;
            // A human confirmed the speaker for the whole entry → verified.
            p.sentences.forEach((s) => {
              s.speakerConfidence = 1;
            });
          });
          // Reassigning to a neighbor's speaker merges the adjacent entries.
          const { paragraphs, survivingUid, removedUids } = coalesceSameSpeaker(
            relabeled.paragraphs,
            uid,
          );
          const draft = { ...relabeled, paragraphs };
          // The label is now referenced by an entry → no longer a pending session speaker.
          const sessionSpeakers = state.sessionSpeakers.filter(
            (s) => s !== label,
          );
          if (removedUids.length === 0) return { draft, sessionSpeakers };
          // Drop absorbed uids; the merged entry's range is the union of already
          // valid ranges, so treat it as confirmed (don't trip the word-gate).
          const kept = state.rangeConfirmed.filter(
            (u) => !removedUids.includes(u),
          );
          return {
            draft,
            sessionSpeakers,
            rangeConfirmed: kept.includes(survivingUid)
              ? kept
              : [...kept, survivingUid],
          };
        });
      },

      verifySpeaker: (uid, sentenceIdx) => {
        set((state) => {
          if (!state.draft) return state;
          return {
            draft: mutateParagraph(state.draft, uid, (p) => {
              const sentence = p.sentences[sentenceIdx];
              // A human confirmed the AI's speaker label is correct → verified.
              if (sentence) sentence.speakerConfidence = 1;
            }),
          };
        });
      },

      deleteEntry: (uid) => {
        set((state) => {
          if (!state.draft) return state;
          const next = clone(state.draft);
          next.paragraphs = next.paragraphs.filter((p) => p._uid !== uid);
          return {
            draft: next,
            rangeConfirmed: state.rangeConfirmed.filter((u) => u !== uid),
          };
        });
      },

      deleteSentence: (uid, sentenceIdx) => {
        set((state) => {
          if (!state.draft) return state;
          const next = clone(state.draft);
          const idx = next.paragraphs.findIndex((p) => p._uid === uid);
          if (idx === -1) return state;
          const para = next.paragraphs[idx];
          if (!para.sentences[sentenceIdx]) return state;

          // Last sentence → drop the whole entry (no empty entries allowed).
          if (para.sentences.length <= 1) {
            next.paragraphs.splice(idx, 1);
            return {
              draft: next,
              rangeConfirmed: state.rangeConfirmed.filter((u) => u !== uid),
            };
          }

          // Remove the sentence and re-time the entry to its remaining span.
          para.sentences.splice(sentenceIdx, 1);
          para.start = Math.min(...para.sentences.map((s) => s.start));
          para.end = Math.max(...para.sentences.map((s) => s.end));
          next.paragraphs = sortByStart(next.paragraphs);
          return {
            draft: next,
            // Range now comes straight from the remaining sentences.
            rangeConfirmed: state.rangeConfirmed.includes(uid)
              ? state.rangeConfirmed
              : [...state.rangeConfirmed, uid],
          };
        });
      },

      addEntry: ({ label, start, end, text }) => {
        const uid = nextUid();
        set((state) => {
          if (!state.draft) return state;
          const next = clone(state.draft);
          const entry: TranscriptParagraph = {
            _uid: uid,
            speaker: label,
            start,
            end,
            sentences: [{ text, start, end }],
          };
          next.paragraphs = sortByStart([...next.paragraphs, entry]);
          return {
            draft: next,
            // A brand-new entry's range was just chosen → considered confirmed.
            rangeConfirmed: [...state.rangeConfirmed, uid],
            sessionSpeakers: state.sessionSpeakers.filter((s) => s !== label),
          };
        });
        return uid;
      },

      setEntryRange: (uid, start, end) => {
        set((state) => {
          if (!state.draft) return state;
          const mutated = mutateParagraph(state.draft, uid, (p) => {
            p.start = start;
            p.end = end;
            // Single-sentence entries track the entry bounds directly.
            if (p.sentences.length === 1) {
              p.sentences[0].start = start;
              p.sentences[0].end = end;
            }
          });
          mutated.paragraphs = sortByStart(mutated.paragraphs);
          return {
            draft: mutated,
            rangeConfirmed: state.rangeConfirmed.includes(uid)
              ? state.rangeConfirmed
              : [...state.rangeConfirmed, uid],
          };
        });
      },

      splitSentenceToSpeaker: (uid, sentenceIdx, label) => {
        // Pre-generate the (up to two) new uids so the counter advances
        // deterministically, regardless of how many segments the split yields.
        const uidMiddle = nextUid();
        const uidAfter = nextUid();
        set((state) => {
          if (!state.draft) return state;
          const next = clone(state.draft);
          const idx = next.paragraphs.findIndex((p) => p._uid === uid);
          if (idx === -1) return state;
          const para = next.paragraphs[idx];
          if (!para.sentences[sentenceIdx]) return state;

          // Only sentence in the entry → just reassign in place (no empty entry).
          if (para.sentences.length <= 1) {
            para.speaker = label;
            // A human confirmed this sentence's speaker → verified.
            para.sentences[sentenceIdx].speakerConfidence = 1;
            return {
              draft: next,
              sessionSpeakers: state.sessionSpeakers.filter((s) => s !== label),
            };
          }

          const before = para.sentences.slice(0, sentenceIdx);
          const middle = para.sentences[sentenceIdx];
          const after = para.sentences.slice(sentenceIdx + 1);
          const { speaker } = para;

          // The middle sentence is the one being reassigned to `label` — a human
          // confirmed its speaker, so treat the speaker label as verified.
          middle.speakerConfidence = 1;

          // Each segment spans its own sentences' actual timestamps.
          const rangeOf = (sentences: typeof para.sentences) => ({
            start: Math.min(...sentences.map((s) => s.start)),
            end: Math.max(...sentences.map((s) => s.end)),
          });

          // Rebuild the source into up to three time-ordered segments. The
          // first segment reuses the source _uid; the rest get fresh ones.
          const segments: TranscriptParagraph[] = [];
          const newUids: string[] = [];
          if (before.length > 0) {
            segments.push({ _uid: uid, speaker, ...rangeOf(before), sentences: before });
            segments.push({
              _uid: uidMiddle,
              speaker: label,
              ...rangeOf([middle]),
              sentences: [middle],
            });
            newUids.push(uidMiddle);
          } else {
            // Reassigning the first sentence: the source entry itself becomes
            // the new-speaker segment.
            segments.push({
              _uid: uid,
              speaker: label,
              ...rangeOf([middle]),
              sentences: [middle],
            });
          }
          if (after.length > 0) {
            segments.push({
              _uid: uidAfter,
              speaker,
              ...rangeOf(after),
              sentences: after,
            });
            newUids.push(uidAfter);
          }

          // Keep the document ordered by start time after re-timing the split.
          next.paragraphs.splice(idx, 1, ...segments);
          next.paragraphs = sortByStart(next.paragraphs);
          return {
            draft: next,
            // Timestamps come straight from the sentences → treat as confirmed
            // (skip the word-gate).
            rangeConfirmed: [...state.rangeConfirmed, ...newUids],
            sessionSpeakers: state.sessionSpeakers.filter((s) => s !== label),
          };
        });
      },

      addSpeaker: (label) => {
        const trimmed = label.trim();
        if (!trimmed) return;
        set((state) => {
          const exists =
            derivedSpeakers(state.draft).includes(trimmed) ||
            state.sessionSpeakers.includes(trimmed);
          if (exists) return state;
          return { sessionSpeakers: [...state.sessionSpeakers, trimmed] };
        });
      },

      renameSpeaker: (oldLabel, newLabel) => {
        const trimmed = newLabel.trim();
        if (!trimmed || trimmed === oldLabel) return;
        set((state) => {
          if (!state.draft) return state;
          // Reject a rename onto an existing label (same check addSpeaker uses):
          // merging two speakers here would silently collapse distinct entries.
          const collides =
            derivedSpeakers(state.draft).includes(trimmed) ||
            state.sessionSpeakers.includes(trimmed);
          if (collides) return state;
          const next = clone(state.draft);
          next.paragraphs.forEach((p) => {
            if (String(p.speaker) === oldLabel) p.speaker = trimmed;
          });
          return {
            draft: next,
            sessionSpeakers: state.sessionSpeakers.map((s) =>
              s === oldLabel ? trimmed : s,
            ),
          };
        });
      },

      renameSpeakers: (renames) => {
        set((state) => {
          if (!state.draft) return state;
          // Normalize: trim targets, drop empties and no-ops.
          const map = new Map<string, string>();
          for (const [oldLabel, newLabel] of Object.entries(renames)) {
            const trimmed = String(newLabel).trim();
            if (trimmed && trimmed !== oldLabel) map.set(oldLabel, trimmed);
          }
          if (map.size === 0) return state;

          // Reject any map that would merge speakers, mirroring renameSpeaker's
          // guard. Two sources sharing a target collapses distinct speakers...
          const targets = [...map.values()];
          if (new Set(targets).size !== targets.length) return state;
          // ...and a target that names an existing speaker merges into it —
          // unless that speaker is itself being renamed away (so it vacates the
          // name), which is what makes swaps (A→B, B→A) and chains (A→B, B→C) ok.
          const currentLabels = new Set([
            ...derivedSpeakers(state.draft),
            ...state.sessionSpeakers,
          ]);
          for (const target of targets) {
            if (currentLabels.has(target) && !map.has(target)) return state;
          }

          // Relabel every paragraph from its original speaker in one pass, so
          // swaps and chains never collide against an intermediate state.
          const next = clone(state.draft);
          next.paragraphs.forEach((p) => {
            const target = map.get(String(p.speaker));
            if (target !== undefined) p.speaker = target;
          });
          return {
            draft: next,
            sessionSpeakers: state.sessionSpeakers.map((s) => map.get(s) ?? s),
          };
        });
      },

      deleteSpeaker: (label) => {
        set((state) => {
          // Referential-integrity guard: cannot delete while any entry uses it.
          const referenced = derivedSpeakers(state.draft).includes(label);
          if (referenced) return state;
          return {
            sessionSpeakers: state.sessionSpeakers.filter((s) => s !== label),
          };
        });
      },
    }),
    {
      // Only the draft participates in undo/redo history.
      partialize: (state) => ({ draft: state.draft }),
      equality: (past, current) =>
        JSON.stringify(past.draft) === JSON.stringify(current.draft),
      limit: 50,
    },
  ),
);

type TranscriptTemporalState = TemporalState<
  Pick<TranscriptDraftState, "draft">
>;

export const useTranscriptDraftTemporal = () =>
  useStoreWithEqualityFn(
    useTranscriptDraftStore.temporal,
    (state: TranscriptTemporalState) => ({
      undo: state.undo,
      redo: state.redo,
      clear: state.clear,
      pastStates: state.pastStates,
      futureStates: state.futureStates,
    }),
    shallow,
  );

export const useCanUndo = () =>
  useStoreWithEqualityFn(
    useTranscriptDraftStore.temporal,
    (state: TranscriptTemporalState) => state.pastStates.length > 0,
  );

export const useCanRedo = () =>
  useStoreWithEqualityFn(
    useTranscriptDraftStore.temporal,
    (state: TranscriptTemporalState) => state.futureStates.length > 0,
  );
